import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@/lib/hooks';
import { api } from '@/lib/api/client';

// eslint_disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>;

interface ApiResponse {
  results?: AnyRecord[];
  data?: AnyRecord[];
}

interface FiltersState {
  limit: number;
  page: number;
  s: string | undefined;
  [key: string]: unknown;
}

const DEFAULT_PARAMS: Record<string, unknown> = {};

interface UseComboboxDataOptions {
  url: string;
  listUrl?: string;
  valueKey: string;
  queryKey: string;
  params?: Record<string, unknown>;
  selectedId?: number | string | undefined;
  defaultSelectedId?: number | string | undefined;
  open: boolean;
  disabled: boolean;
  onSelectItem?: (item: AnyRecord | null) => void;
  notifyOnPreselect?: boolean;
}

export function useComboboxData({
  url,
  listUrl,
  valueKey,
  queryKey,
  params = DEFAULT_PARAMS,
  selectedId,
  defaultSelectedId,
  open,
  disabled,
  onSelectItem,
  notifyOnPreselect = true,
}: UseComboboxDataOptions) {
  const effectiveListUrl = listUrl ?? url;
  const [inputValue, setInputValue] = React.useState('');
  const debouncedInput = useDebounce(inputValue, 300);
  const userOverrodeRef = React.useRef(false);

  const [internalId, setInternalId] = React.useState<number | string | undefined>(() => {
    if (defaultSelectedId) return defaultSelectedId;
    if (selectedId) return selectedId;
    return undefined;
  });

  React.useEffect(() => {
    if (!selectedId) {
      setInternalId(undefined);
      userOverrodeRef.current = false;
    } else if (!userOverrodeRef.current) {
      setInternalId(selectedId);
    }
  }, [selectedId]);

  const [filters, setFilters] = React.useState<FiltersState>({
    limit: 50,
    page: 0,
    s: undefined,
    ...params,
  });

  const paramsString = React.useMemo(() => JSON.stringify(params), [params]);

  React.useEffect(() => {
    const parsedParams = JSON.parse(paramsString || '{}') as Record<string, unknown>;
    setFilters((prev) => {
      const next: FiltersState = {
        ...prev,
        ...parsedParams,
        page: 0,
        s: debouncedInput.length >= 2 ? debouncedInput : undefined,
      };
      if (JSON.stringify(next) === JSON.stringify(prev)) return prev;
      return next;
    });
  }, [paramsString, debouncedInput]);

  const qKey = React.useMemo(
    () => [queryKey, effectiveListUrl, filters.limit, filters.page, filters.s, paramsString],
    [queryKey, effectiveListUrl, filters.limit, filters.page, filters.s, paramsString]
  );

  const { data, isFetching } = useQuery({
    queryKey: qKey,
    queryFn: async () => {
      const offset = filters.page * filters.limit;
      const queryParams: Record<string, unknown> = { ...filters, offset };
      delete queryParams['page'];
      Object.keys(queryParams).forEach((k) => {
        if (queryParams[k] == null || queryParams[k] === '') {
          delete queryParams[k];
        }
      });
      const res = await api.get<ApiResponse | AnyRecord[]>(effectiveListUrl, { params: queryParams });
      return res;
    },
    enabled: open && !disabled,
    staleTime: 5 * 60 * 1000,
  });

  const items: AnyRecord[] = React.useMemo(() => {
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && 'results' in data) {
      return (data as ApiResponse).results ?? [];
    }
    return [];
  }, [data]);

  const cacheRef = React.useRef(new Map<string, AnyRecord>());

  React.useEffect(() => {
    if (!items.length) return;
    for (const it of items) {
      const idVal = it[valueKey];
      if (idVal != null) {
        cacheRef.current.set(String(idVal), it);
      }
    }
  }, [items, valueKey]);

  const [, setTick] = React.useState(0);
  const forceRerender = () => setTick((x) => x + 1);

  const notifiedRef = React.useRef(new Set<number | string>());

  const fetchOne = React.useCallback(
    async (id: number | string): Promise<AnyRecord | null> => {
      if (!id) return null;
      try {
        const res = await api.get<AnyRecord>(`${url}/${String(id)}`);
        // Resolving a *preselected* id is hydration, not a user choice. Callers
        // whose onSelectItem applies side effects (prefilling other fields) must
        // opt out, or the echo fires mid-edit — whenever this request happens to
        // resolve — and overwrites whatever the user has already typed.
        if (notifyOnPreselect && !notifiedRef.current.has(id)) {
          onSelectItem?.(res);
          notifiedRef.current.add(id);
        }
        return res;
      } catch {
        return null;
      }
    },
    [url, onSelectItem, notifyOnPreselect]
  );

  React.useEffect(() => {
    if (!internalId) return;
    const key = String(internalId);
    if (cacheRef.current.has(key)) return;

    let cancelled = false;
    void (async () => {
      const entity = await fetchOne(internalId);
      if (!cancelled && entity && entity[valueKey] != null) {
        cacheRef.current.set(String(entity[valueKey]), entity);
        forceRerender();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [internalId, valueKey, fetchOne]);

  const selectedItem = !internalId ? null : cacheRef.current.get(String(internalId)) ?? null;

  const handleSelect = (item: AnyRecord | null) => {
    if (!item) {
      userOverrodeRef.current = true;
      setInternalId(undefined);
      onSelectItem?.(null);
      return null;
    }

    const idVal = item[valueKey] as number | string;
    if (idVal == null) return null;

    const isSame = String(internalId ?? '') === String(idVal);
    userOverrodeRef.current = true;

    if (isSame) {
      setInternalId(undefined);
      onSelectItem?.(null);
      return null;
    } else {
      cacheRef.current.set(String(idVal), item);
      setInternalId(idVal);
      onSelectItem?.(item);
      return idVal;
    }
  };

  const resetInput = () => {
    setInputValue('');
    setFilters((prev) => ({ ...prev, s: undefined, page: 0 }));
  };

  return {
    inputValue,
    setInputValue,
    items,
    isFetching,
    selectedItem,
    handleSelect,
    resetInput,
  };
}
